import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getSignedMediaUrl } from '../services/api';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Viewer pentru PowerPoint convertit în PDF: afișează UN SINGUR slide pe ecran,
// fit-to-width, fără scroll vertical. Navigare cu săgeți mari ←/→ + tastele săgeți.
const PowerPointViewer = ({ url, onProgress }) => {
  const { t } = useTranslation();
  const [numSlides, setNumSlides] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxSlideReached, setMaxSlideReached] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });

  // URL semnat (autorizat) pentru prezentarea protejată. Se obține asincron prin /media/sign.
  const [resolvedUrl, setResolvedUrl] = useState(null);
  useEffect(() => {
    let active = true;
    setResolvedUrl(null);
    getSignedMediaUrl(url).then((u) => {
      if (active) setResolvedUrl(u);
    });
    return () => {
      active = false;
    };
  }, [url]);

  useEffect(() => {
    setNumSlides(null);
    setCurrentSlide(1);
    setLoading(true);
    setError(null);
    setMaxSlideReached(0);
  }, [url]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const changeSlide = useCallback(
    (slide) => {
      if (!numSlides || slide < 1 || slide > numSlides) return;
      setCurrentSlide(slide);
      const newMax = Math.max(maxSlideReached, slide);
      if (newMax > maxSlideReached) {
        setMaxSlideReached(newMax);
        if (onProgress) onProgress({ current: newMax, total: numSlides });
      }
    },
    [numSlides, maxSlideReached, onProgress]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && currentSlide > 1) changeSlide(currentSlide - 1);
      if (e.key === 'ArrowRight' && numSlides && currentSlide < numSlides)
        changeSlide(currentSlide + 1);
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentSlide, numSlides, changeSlide, isFullscreen]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: total }) => {
      setNumSlides(total);
      setLoading(false);
      setError(null);
      setMaxSlideReached(1);
      if (onProgress) onProgress({ current: 1, total });
    },
    [onProgress]
  );

  const onDocumentLoadError = useCallback(
    (err) => {
      console.error('PPT load error:', err);
      setError(t('viewers.ppt.loadError'));
      setLoading(false);
    },
    [t]
  );

  if (!url) return null;

  // Slide-urile PowerPoint au de obicei aspect 16:9 (sau 4:3). Folosim înălțimea disponibilă
  // ca limită, dar nu lăsăm slide-ul să iasă din lățimea containerului.
  // Lăsăm spațiu pentru toolbar (~50px) + butoane navigare (~64px lateral *2 dar overlay).
  const availableHeight = Math.max(containerSize.height - 60, 300);
  const availableWidth = Math.max(containerSize.width - 160, 300);
  // Asumăm 16:9 ca aspect default — pdf.js redă oricum pagina la dimensiunea ei reală scalată.
  const slideWidthByHeight = availableHeight * (16 / 9);
  const slideWidth = Math.min(slideWidthByHeight, availableWidth);

  const allViewed = numSlides && maxSlideReached >= numSlides;

  return (
    <div
      ref={containerRef}
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-slate-900 overflow-hidden'
          : 'relative flex flex-col bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden'
      }
      style={isFullscreen ? { height: '100vh', width: '100vw' } : { height: '65vh' }}
    >
      {/* Toolbar sus */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700 flex-shrink-0 text-slate-100">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-orange-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M16.5 3.75h.008v.008H16.5V3.75z"
            />
          </svg>
          <span className="text-sm font-medium">{t('viewers.ppt.title')}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm tabular-nums select-none">
            {t('viewers.ppt.slideCounter', { current: currentSlide, total: numSlides || '...' })}
          </span>
          {numSlides && numSlides > 1 && (
            <div className="w-24 bg-slate-700 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-1.5 rounded-full transition-all duration-300 ${allViewed ? 'bg-green-400' : 'bg-orange-400'}`}
                style={{ width: `${(maxSlideReached / numSlides) * 100}%` }}
              />
            </div>
          )}
          <button
            onClick={() => setIsFullscreen((f) => !f)}
            className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors text-slate-200"
            title={isFullscreen ? t('viewers.exitFullscreen') : t('viewers.fullscreen')}
          >
            {isFullscreen ? (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Slide central */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-slate-900">
        {error ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-300">
            <svg
              className="w-12 h-12 mb-3 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <p className="text-sm font-medium">{error}</p>
            <a
              href={resolvedUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 text-sm text-blue-400 hover:underline"
            >
              {t('viewers.ppt.openFile')}
            </a>
          </div>
        ) : !resolvedUrl ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-400 border-t-transparent" />
          </div>
        ) : (
          <Document
            key={url}
            file={resolvedUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-400 border-t-transparent" />
              </div>
            }
          >
            <div className="shadow-2xl rounded bg-white" style={{ width: 'fit-content' }}>
              <Page
                pageNumber={currentSlide}
                width={slideWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div
                    className="flex items-center justify-center"
                    style={{ width: slideWidth, height: (slideWidth * 9) / 16 }}
                  >
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-400 border-t-transparent" />
                  </div>
                }
              />
            </div>
          </Document>
        )}

        {/* Săgeată stânga (overlay) */}
        <button
          onClick={() => changeSlide(currentSlide - 1)}
          disabled={currentSlide <= 1 || loading}
          aria-label={t('viewers.ppt.prevSlide')}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center transition-all disabled:opacity-20 disabled:cursor-not-allowed shadow-lg"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Săgeată dreapta (overlay) */}
        <button
          onClick={() => changeSlide(currentSlide + 1)}
          disabled={!numSlides || currentSlide >= numSlides || loading}
          aria-label={t('viewers.ppt.nextSlide')}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center transition-all disabled:opacity-20 disabled:cursor-not-allowed shadow-lg"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Hint jos */}
      <div className="px-4 py-1.5 bg-slate-800 border-t border-slate-700 flex-shrink-0">
        <p className="text-xs text-slate-400 text-center">{t('viewers.ppt.hint')}</p>
      </div>
    </div>
  );
};

export default PowerPointViewer;
